import { useState, useMemo, useRef, useCallback } from 'react';

interface IUseShowMoreOptions {
  initialCount?: number;
  incrementCount?: number;
}

/**
 * Хук для постепенной загрузки списков с кнопкой "Показать еще"
 * @param items - массив элементов для отображения
 * @param options - настройки (начальное количество и шаг увеличения)
 */
export const useShowMore = <T>(items: T[] | undefined, options?: IUseShowMoreOptions) => {
  const { initialCount = 10, incrementCount = 10 } = options || {};

  const [visibleCount, setVisibleCount] = useState(initialCount);
  const [isRendering, setIsRendering] = useState(false);
  const pendingTimerRef = useRef<number | null>(null);

  const visibleItems = useMemo(() => {
    if (!items) return [];

    return items.slice(0, visibleCount);
  }, [items, visibleCount]);

  const hasMore = useMemo(() => {
    return items ? items.length > visibleCount : false;
  }, [items, visibleCount]);

  const showMore = useCallback(() => {
    setIsRendering(true);

    if (pendingTimerRef.current) {
      clearTimeout(pendingTimerRef.current);
    }
    requestAnimationFrame(() => {
      pendingTimerRef.current = window.setTimeout(() => {
        setVisibleCount(prev => prev + incrementCount);
        setIsRendering(false);
        pendingTimerRef.current = null;
      }, 50);
    });
  }, [incrementCount]);

  const showAll = useCallback(() => {
    if (items) {
      setIsRendering(true);
      if (pendingTimerRef.current) {
        clearTimeout(pendingTimerRef.current);
      }
      requestAnimationFrame(() => {
        pendingTimerRef.current = window.setTimeout(() => {
          setVisibleCount(items.length);
          setIsRendering(false);
          pendingTimerRef.current = null;
        }, 50);
      });
    }
  }, [items]);

  const reset = useCallback(() => {
    setVisibleCount(initialCount);
    setIsRendering(false);
    if (pendingTimerRef.current) {
      clearTimeout(pendingTimerRef.current);
      pendingTimerRef.current = null;
    }
  }, [initialCount]);

  return {
    visibleItems,
    hasMore,
    showMore,
    showAll,
    reset,
    totalCount: items?.length || 0,
    visibleCount,
    isRendering,
  };
};
